import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, trackEvent, trackPixel } from "@/lib/analytics";

// Public routes where Meta Pixel ViewContent is meaningful (high-intent content).
const VIEW_CONTENT_ROUTES = [
  { match: (p) => p === "/", type: "landing", name: "Emad Landing" },
  { match: (p) => p === "/opportunities", type: "opportunities_list", name: "Opportunities List" },
  { match: (p) => p === "/partners", type: "partners", name: "Partners" },
];

/**
 * Global analytics wiring — mount once inside <BrowserRouter>.
 *
 * 1. Fires GA4 `page_view` on every SPA route change.
 * 2. Fires Meta Pixel `PageView` on every SPA route change (initial + subsequent).
 * 3. Fires Meta Pixel `ViewContent` on high-intent public pages.
 * 4. Fires Meta Pixel `Lead` when visitor lands on /register (clear interest).
 * 5. Delegates a single document-level click listener so every element with
 *    a `data-testid` produces a `ui_click` event automatically.
 */
let lastMetaPath = null;

export default function AnalyticsTracker() {
  const location = useLocation();

  // SPA page_view (GA + Pixel)
  useEffect(() => {
    const path = location.pathname + location.search;
    trackPageView(path);
    if (lastMetaPath === path) return;
    lastMetaPath = path;
    trackPixel("PageView");

    // High-intent ViewContent
    const match = VIEW_CONTENT_ROUTES.find((r) => r.match(location.pathname));
    if (match) {
      trackPixel("ViewContent", {
        content_type: match.type,
        content_name: match.name,
        content_category: "emad",
      });
    }

    // Lead — visitor reached the sign-up form (clear interest).
    if (location.pathname === "/register") {
      const params = new URLSearchParams(location.search);
      trackPixel("Lead", {
        content_category: "signup_intent",
        role: params.get("role") || "unknown",
      });
    }
  }, [location.pathname, location.search]);

  // Global click delegation on any interactive element with data-testid.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onClick = (event) => {
      const el = event.target?.closest?.("[data-testid]");
      if (!el) return;
      const testid = el.getAttribute("data-testid");
      if (!testid) return;
      const tag = (el.tagName || "").toLowerCase();
      // Restrict to real click targets — skip pure text/labels.
      const isInteractive =
        tag === "button" ||
        tag === "a" ||
        tag === "input" ||
        tag === "select" ||
        tag === "label" ||
        el.getAttribute("role") === "button" ||
        el.getAttribute("role") === "link" ||
        el.getAttribute("role") === "radio" ||
        el.getAttribute("role") === "menuitem";
      if (!isInteractive) return;
      const label = (el.getAttribute("aria-label") || el.innerText || "").trim().slice(0, 80);
      trackEvent("ui_click", {
        testid,
        tag,
        label,
        path: window.location.pathname,
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}
